import { Transition, TransitionChild } from '@headlessui/react';
import {
  ChevronDownIcon,
  ChevronRightDoubleIcon,
  ChevronUpIcon,
  Expand06Icon,
} from '@langchain/untitled-ui-icons';
import * as Portal from '@radix-ui/react-portal';

import {
  Fragment,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { KeyboardShortcut } from '@/Pages/KeyboardShortcutsSidePane';
import { IconButton } from '@/design-system';

import { cn } from '../utils/tailwind';
import { createPortalSlot } from './PortalSlot';

const PaneDepth = createContext<number>(0);

const MINIMUM_LEFT_MARGIN = 100;
const MINIMUM_RIGHT_MARGIN = 500;

const MAX_INITIAL_RIGHT_MARGIN = 1280;
const INITIAL_RIGHT_MARGIN_RATIO = 2 / 3;

const getClientWidth = () =>
  Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);

function clampLeft(value: number) {
  const clientWidth = getClientWidth();
  return Math.max(
    MINIMUM_LEFT_MARGIN,
    Math.min(clientWidth - MINIMUM_RIGHT_MARGIN, value)
  );
}

function defaultLeft(props: {
  offset: number;
  sidePaneId?: string | undefined;
  defaultWidth?: number;
}) {
  const clientWidth = getClientWidth();
  if (props.sidePaneId != null) {
    const value = window.localStorage.getItem(
      `SidePanelGroup:sizes:${props.sidePaneId}`
    );
    const pastRatio = Number.parseFloat(value ?? '');

    if (!Number.isNaN(pastRatio)) {
      const desiredWidth = clientWidth * pastRatio - props.offset;
      return clampLeft(clientWidth - desiredWidth);
    }
  }

  const desiredWidth =
    Math.min(
      props.defaultWidth ?? MAX_INITIAL_RIGHT_MARGIN,
      clientWidth * INITIAL_RIGHT_MARGIN_RATIO
    ) - props.offset;

  return clampLeft(clientWidth - desiredWidth);
}

function useShowChildren({
  open,
  children,
}: {
  open: boolean;
  children: ReactNode;
}) {
  const [showChildren, setShowChildren] = useState<ReactNode>(children);
  useEffect(() => {
    // initial render would hide the children
    // we only want to preserve children when closing
    if (open || !!showChildren === open) {
      return setShowChildren(children);
    }
    // This ensures the transition starts before children are rendered,
    // which helps with avoidiing stuttering.
    const timeout = setTimeout(
      () => setShowChildren(open ? null : children),
      open ? 200 : 0
    );
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, children]);
  return showChildren;
}

function useResize(props: {
  offset: number;
  sidePaneId?: string | undefined;
  defaultWidth?: number;
  overrideWidth?: number;
}) {
  const target = useRef<HTMLDivElement>(null);
  const container = useRef<HTMLDivElement>(null);

  const resizing = useRef(false);
  const [left, setLeft] = useState(() => defaultLeft(props));

  useEffect(() => {
    const clientWidth = getClientWidth();
    if (props.overrideWidth) {
      const desiredWidth = props.overrideWidth - props.offset;

      setLeft(clampLeft(clientWidth - desiredWidth));
    }
  }, [props.overrideWidth, props.offset]);

  const onMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    resizing.current = true;
    if (target.current) {
      target.current.dataset.resizeHandleActive = 'true';
    }
  }, []);

  const onMouseUp = useCallback(() => {
    if (resizing.current) {
      resizing.current = false;
      if (target.current) {
        delete target.current.dataset.resizeHandleActive;
      }
      if (container.current) {
        setLeft(container.current?.getBoundingClientRect().left);
      }
    }
  }, []);

  const doResizeBounded = useCallback(
    (desiredX: number) => {
      if (container.current) {
        const bounded = clampLeft(desiredX);

        if (props.sidePaneId != null) {
          const clientWidth = getClientWidth();
          const ratio = (clientWidth - bounded) / clientWidth;
          window.localStorage.setItem(
            `SidePanelGroup:sizes:${props.sidePaneId}`,
            ratio.toString()
          );
        }

        container.current.style.left = `${bounded}px`;
        return bounded;
      }
    },
    [props.sidePaneId]
  );

  const onMouseMove = useCallback(
    (e: MouseEvent) => {
      if (resizing.current && container.current) {
        e.preventDefault();
        doResizeBounded(e.clientX);
      }
    },
    [doResizeBounded]
  );

  const onResize = useCallback(() => {
    if (container.current) {
      const bounded = doResizeBounded(
        container.current.getBoundingClientRect().left
      );
      if (bounded) {
        setLeft(bounded);
      }
    }
  }, [doResizeBounded]);

  useEffect(() => {
    window.addEventListener('resize', onResize);
    document.addEventListener('mouseup', onMouseUp);
    document.addEventListener('mousemove', onMouseMove);
    return () => {
      window.removeEventListener('resize', onResize);
      document.removeEventListener('mouseup', onMouseUp);
      document.removeEventListener('mousemove', onMouseMove);
    };
  }, [onMouseMove, onMouseUp, onResize]);

  return { left, onMouseDown, target, container };
}

export const HeaderTitleActionSlot = createPortalSlot();

const Header = ({
  title,
  onClose,
  onExpand,
  onNext,
  onPrevious,
  hideArrows,
}: {
  title: ReactNode;
  onClose?: () => void;
  onExpand?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onNext?: () => void;
  onPrevious?: () => void;
  hideArrows?: boolean;
}) => {
  return (
    <div className="sticky top-0 z-10 flex items-center gap-1 border-b border-secondary bg-background px-3 py-3 text-lg font-medium dark:text-[#B0B0B0]">
      <IconButton
        label="Close"
        icon={ChevronRightDoubleIcon}
        onClick={onClose}
        variant="plain"
        color="secondary"
        size="sm"
        iconClassName="size-6 text-quaternary"
        data-testid="split-view-pane-close-button"
      />

      {onExpand && (
        <IconButton
          label="Expand"
          icon={Expand06Icon}
          onClick={onExpand}
          variant="plain"
          color="secondary"
          size="sm"
          iconClassName="size-5 text-quaternary"
          data-testid="split-view-pane-expand-button"
        />
      )}
      {!hideArrows && (
        <>
          <div className="mx-2 h-[32px] self-stretch border-r border-r-secondary" />
          <IconButton
            label="Navigate down"
            tooltipProps={{
              title: (
                <div className="flex items-center">
                  <span>Navigate down</span>
                  <KeyboardShortcut keys={['j']} />
                </div>
              ),
            }}
            icon={ChevronDownIcon}
            onClick={onNext}
            variant="plain"
            color="secondary"
            size="sm"
            iconClassName="size-6 text-quaternary"
            disabled={!onNext}
          />

          <IconButton
            label="Navigate up"
            tooltipProps={{
              title: (
                <div className="flex items-center">
                  <span>Navigate up</span>
                  <KeyboardShortcut keys={['k']} />
                </div>
              ),
            }}
            icon={ChevronUpIcon}
            onClick={onPrevious}
            variant="plain"
            color="secondary"
            size="sm"
            iconClassName="size-6 text-quaternary"
            disabled={!onPrevious}
          />
        </>
      )}
      {title}

      <HeaderTitleActionSlot.Slot className="flex flex-1 justify-end empty:hidden" />
    </div>
  );
};

export function SplitViewPane({
  open,
  onClose,
  onExpand,
  onNext,
  onPrevious,
  children,
  title,
  className,
  sidePaneId,
  hideArrows,
  customHeader,
  withOverlay = false,
  defaultWidth,
  overrideWidth,
}: {
  open: boolean;
  children: ReactNode;
  className?: string;
  sidePaneId?: string | undefined;
  hideArrows?: boolean;
  withOverlay?: boolean;
  defaultWidth?: number;
  overrideWidth?: number;
} & (
  | {
      customHeader: ReactNode;
      title?: undefined;
      onClose?: undefined;
      onExpand?: undefined;
      onNext?: undefined;
      onPrevious?: undefined;
    }
  | {
      customHeader?: undefined;
      title: ReactNode;
      onClose: () => void;
      onExpand?: (e: React.MouseEvent<HTMLButtonElement>) => void;
      onNext?: () => void;
      onPrevious?: () => void;
    }
)) {
  const depth = useContext(PaneDepth);
  const showChildren = useShowChildren({ children, open });
  const { left, onMouseDown, target, container } = useResize({
    offset: depth * 4.5 * 16,
    sidePaneId,
    defaultWidth,
    overrideWidth,
  });

  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    function listener(event: KeyboardEvent) {
      const annotationPane = document.querySelector(
        '[data-testid="run-annotations-crud-pane"]'
      );
      if (event.key === 'Escape' && !annotationPane) onCloseRef.current?.();
    }

    document.addEventListener('keydown', listener);
    return () => document.removeEventListener('keydown', listener);
  }, [open]);

  const nextRef = useRef(onNext);
  nextRef.current = onNext;

  const prevRef = useRef(onPrevious);
  prevRef.current = onPrevious;

  useEffect(() => {
    function keypress(e: KeyboardEvent) {
      if (!open) return;
      if (e.target instanceof HTMLElement) {
        const tagName = e.target.tagName.toLowerCase();
        if (
          tagName === 'textarea' ||
          tagName === 'input' ||
          e.target.hasAttribute('contenteditable')
        )
          return;
      }

      if (e.key === 'j') {
        if (nextRef.current) {
          e.stopImmediatePropagation();
          nextRef.current?.();
        }
      } else if (e.key === 'k') {
        if (prevRef.current) {
          e.stopImmediatePropagation();
          prevRef.current?.();
        }
      }
    }

    document.addEventListener('keypress', keypress);
    return () => document.removeEventListener('keypress', keypress);
  }, [open]);

  const renderHeader = () => {
    if (customHeader) {
      return customHeader;
    }
    return (
      <Header
        title={title}
        onClose={onClose}
        onExpand={onExpand}
        onNext={onNext}
        onPrevious={onPrevious}
        hideArrows={hideArrows}
      />
    );
  };

  return (
    <HeaderTitleActionSlot.Context>
      <Portal.Root>
        <Transition show={open} as={Fragment}>
          <div className="relative z-10">
            {withOverlay && (
              <TransitionChild
                as={'div'}
                enter="ease-out duration-200"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="fixed inset-0 bg-black/30" />
              </TransitionChild>
            )}
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="ease-in duration-200"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <div
                ref={container}
                style={{ left }}
                className={cn('fixed inset-0 overflow-y-auto shadow-xl')}
                data-testid="split-view-pane"
              >
                {/* The actual dialog panel  */}
                <div className="relative flex min-h-full flex-col items-stretch bg-[var(--joy-palette-background-body)]">
                  <div
                    ref={target}
                    className={cn(
                      'absolute bottom-0 left-0 top-0 z-20 flex w-4 cursor-col-resize flex-col justify-center border-l border-secondary transition-all hover:border-l-2 data-[resize-handle-active]:border-l-brand'
                    )}
                    onMouseDown={onMouseDown}
                  />
                  {renderHeader()}

                  <PaneDepth.Provider value={depth + 1}>
                    <div className={cn('flex-1', className)}>
                      {showChildren}
                    </div>
                  </PaneDepth.Provider>
                </div>
              </div>
            </TransitionChild>
          </div>
        </Transition>
      </Portal.Root>
    </HeaderTitleActionSlot.Context>
  );
}
